import React, { useId, useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { BereichLink } from '../pages/einstellungen/EinstLink';
import { COMMENT_MAX, RATING_CATEGORIES, RatingCategory, Verdict, commentProblem, ratingNotice, ratingPayload } from './answerRatingRules';
import { hilfeBereichGebaut } from './hilfe/hilfeLeiste';
import { antwortMeldenAdresse } from './hilfe/ticketLogik';
import { TICKET_TEXTE } from './hilfe/ticketTexte';

// Paket 18 (RUE): die eigene Bewertung einer Antwort. Produktfläche an der Leiste der Antwort, kein Menüeintrag.
// Fehler des Servers (auch 403 OPERATOR_VIEW_*) erscheinen als Text, kein Toast.
type Saved = { verdict: Verdict; category: string | null } | null;

// Klassen des Kopierknopfs (RISAssistant.tsx); gedrückt in dessen Hover-Farben. Kein neues Gestaltungsmuster.
// Schlusslauf (pruefung-slb M2, Regeln 09 und 12): mindestens 44 × 44 px und 14 px Schrift.
const KNOPF = 'flex items-center justify-center gap-1 min-h-11 min-w-11 px-3 py-2 text-sm hover:text-primary dark:hover:text-white border border-warm-200 dark:border-gray-600 rounded-lg hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50';
const RUHIG = 'text-muted dark:text-gray-400';
const GEDRUECKT = 'text-primary dark:text-white bg-warm-100 dark:bg-gray-700';

const asCategory = (value: unknown): RatingCategory | null =>
  RATING_CATEGORIES.find(([key]) => key === value)?.[0] ?? null;
const serverText = (error: any, fallback: string): string =>
  typeof error?.response?.data?.error === 'string' && error.response.data.error ? error.response.data.error : fallback;

// aktion: ein weiterer Knopf am Ende der Knopfzeile (Kopieren), damit das Formular die ganze Breite der Leiste hat.
const AnswerRating: React.FC<{ researchId: string; initial: Saved; aktion?: React.ReactNode }> = ({ researchId, initial, aktion }) => {
  const brand = useBrand();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const id = useId();
  const [saved, setSaved] = useState<Saved>(initial);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<RatingCategory | null>(asCategory(initial?.category));
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const path = `/api/ris/answers/${encodeURIComponent(researchId)}/rating`;
  const problem = commentProblem(comment);
  // F-23 (PF-NB-hilfe-3, Nutzerblick Firma Z-04): nach „nicht hilfreich“ der Weg zum Support; die Bewertung bleibt anonym.
  const melden = saved?.verdict === 'DOWN' && !open && !user?.sicht?.aktiv && hilfeBereichGebaut('tickets');

  const save = async (verdict: Verdict) => {
    setBusy(true); setError(''); setStatus('');
    try {
      const body = ratingPayload(verdict, category, comment);
      await apiClient.put(path, body);
      setSaved({ verdict, category: body.category });
      if (verdict === 'UP') { setCategory(null); setComment(''); }
      setOpen(false);
      setStatus('Danke für Ihre Rückmeldung.');
    } catch (err) {
      setError(serverText(err, 'Die Bewertung konnte nicht gespeichert werden.'));
    } finally { setBusy(false); }
  };

  const helpful = () => {
    // Eine gespeicherte Zustimmung wird nicht erneut geschrieben; zurücknehmen geht über den eigenen Knopf.
    if (saved?.verdict === 'UP') { setOpen(false); return; }
    void save('UP');
  };

  const toggleUnhelpful = async () => {
    setError(''); setStatus('');
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (saved?.verdict !== 'DOWN') return;
    // Der Verlauf liefert nur Daumen und Kategorie; den eigenen Kommentar lädt erst das Öffnen.
    setBusy(true);
    try {
      const { data } = await apiClient.get(path);
      const rating = data?.rating;
      if (!rating) { setSaved(null); setCategory(null); setComment(''); return; }
      setCategory(asCategory(rating.category));
      setComment(typeof rating.comment === 'string' ? rating.comment : '');
    } catch (err) {
      setError(serverText(err, 'Die Bewertung konnte nicht geladen werden.'));
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true); setError(''); setStatus('');
    try {
      await apiClient.delete(path);
      setSaved(null); setOpen(false); setCategory(null); setComment('');
      setStatus('Bewertung zurückgenommen.');
    } catch (err) {
      setError(serverText(err, 'Die Bewertung konnte nicht zurückgenommen werden.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="w-full min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-label="Antwort hilfreich"
          aria-pressed={saved?.verdict === 'UP'}
          disabled={busy}
          onClick={helpful}
          className={`${KNOPF} ${saved?.verdict === 'UP' ? GEDRUECKT : RUHIG}`}
        >
          <ThumbsUp className="w-4 h-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Antwort nicht hilfreich"
          aria-pressed={saved?.verdict === 'DOWN'}
          aria-expanded={open}
          aria-controls={`${id}-rueckmeldung`}
          disabled={busy && !open}
          onClick={() => void toggleUnhelpful()}
          className={`${KNOPF} ${saved?.verdict === 'DOWN' ? GEDRUECKT : RUHIG}`}
        >
          <ThumbsDown className="w-4 h-4" aria-hidden="true" />
        </button>
        {saved && !open && (
          <button type="button" disabled={busy} onClick={() => void withdraw()} className={`${KNOPF} ${RUHIG}`}>
            Bewertung zurücknehmen
          </button>
        )}
        {melden && <BereichLink to={antwortMeldenAdresse(researchId, pathname)}
          className="inline-flex items-center min-h-11 text-sm font-medium text-primary dark:text-gray-100 underline underline-offset-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
          {TICKET_TEXTE.antwortMelden}
        </BereichLink>}
        <span role="status" className="text-sm text-muted dark:text-gray-400">{status}</span>
        {aktion && <div className="ml-auto shrink-0">{aktion}</div>}
      </div>

      {error && <p role="alert" className="text-sm text-red-700 dark:text-red-400">{error}</p>}

      {open && (
        <form
          id={`${id}-rueckmeldung`}
          className="space-y-2"
          onSubmit={event => { event.preventDefault(); if (!problem) void save('DOWN'); }}
        >
          <div role="radiogroup" aria-labelledby={`${id}-frage`} className="space-y-1">
            <p id={`${id}-frage`} className="text-sm font-medium text-primary dark:text-gray-100">Was stimmt nicht?</p>
            {RATING_CATEGORIES.map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 min-h-11 text-sm text-primary dark:text-gray-100">
                <input
                  type="radio"
                  className="w-5 h-5 shrink-0"
                  name={`${id}-kategorie`}
                  value={key}
                  checked={category === key}
                  disabled={busy}
                  onChange={() => setCategory(key)}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-kommentar`} className="block text-sm font-medium text-primary dark:text-gray-100">
              Kommentar (optional, nur für Sie sichtbar)
            </label>
            <textarea
              id={`${id}-kommentar`}
              value={comment}
              maxLength={COMMENT_MAX}
              rows={3}
              disabled={busy}
              aria-describedby={`${id}-hinweis`}
              onChange={event => setComment(event.target.value)}
              className="w-full px-3 py-2 text-sm bg-transparent text-primary dark:text-gray-100 border border-warm-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            />
            {problem && <p className="text-sm text-red-700 dark:text-red-400">{problem}</p>}
          </div>
          <small id={`${id}-hinweis`} className="block text-sm text-muted dark:text-gray-400">{ratingNotice(brand.displayName)}</small>
          <button type="submit" disabled={busy || !!problem} className={`${KNOPF} ${RUHIG}`}>
            Rückmeldung senden
          </button>
        </form>
      )}
    </div>
  );
};

export default AnswerRating;
